<script>
import DashboardApexChart from './DashboardApexChart.vue';
import dashboardKpiApi from './mixins/dashboardKpiApi';
import dashboardPeriodToolbar from './mixins/dashboardPeriodToolbar';
import dashboardNumberFormat from './mixins/dashboardNumberFormat';

export default {
    name: 'OrdersByPortalViewer',
    components: {
        DashboardApexChart,
    },
    mixins: [dashboardKpiApi, dashboardPeriodToolbar, dashboardNumberFormat],
    props: {
        autoFetch: {
            type: Boolean,
            default: false,
        },
        chartable: {
            type: Boolean,
            default: true,
        },
        chartType: {
            type: String,
            default: 'line',
        },
    },
    computed: {
        portalRows() {
            return this.kpiPayload?.meta?.by_portal ?? [];
        },
        columns() {
            return this.kpiPayload?.meta?.columns ?? [
                'portal',
                'created',
                'shipped',
                'order_value',
                'shipping_value',
            ];
        },
        kpiChart() {
            return this.kpiPayload?.chart ?? null;
        },
        heroValue() {
            return this.kpiPayload?.hero_metric?.value ?? 0;
        },
        heroLabel() {
            return this.kpiPayload?.hero_metric?.label ?? '';
        },
    },
    mounted() {
        if (!this.from && !this.to && this.preset !== 'all') {
            this.setPresetDates(this.preset);
        }
        this.fetchKpi(this.buildPeriodParams());
    },
    methods: {
        formatCell(column, row) {
            if (column === 'portal') {
                return row[column];
            }

            return this.formatKpiValue(row[column], column);
        },
        columnLabel(column) {
            const labelKeys = {
                portal: 'orders_by_portal.column.portal',
                created: 'orders_by_portal.column.created',
                shipped: 'orders_by_portal.column.shipped',
                order_value: 'orders_by_portal.column.order_value',
                shipping_value: 'orders_by_portal.column.shipping_value',
            };

            const key = labelKeys[column];
            if (!key) {
                return column;
            }

            return this._t(key);
        },
    },
};
</script>

<template>
    <div class="orders-by-portal-viewer">
        <div class="kpi-viewer__toolbar">
            <select
                v-model="preset"
                class="form-control input-sm kpi-viewer__preset"
                @change="onPresetChange"
            >
                <option value="7">{{ presetLabels.last7 }}</option>
                <option value="30">{{ presetLabels.last30 }}</option>
                <option value="90">{{ presetLabels.last90 }}</option>
                <option value="year">{{ presetLabels.year }}</option>
                <option value="all">{{ presetLabels.all }}</option>
            </select>
            <button
                type="button"
                class="btn btn-default btn-sm kpi-viewer__refresh"
                :disabled="kpiLoading"
                @click="refreshPeriod"
            >
                <i class="fa" :class="kpiLoading ? 'fa-spinner fa-spin' : 'fa-refresh'" />
            </button>
        </div>

        <div v-if="kpiError" class="kpi-viewer__error alert alert-danger">{{ kpiError }}</div>
        <div class="kpi-stage" :aria-busy="kpiLoading ? 'true' : 'false'">
            <div class="row kpi-summary">
                <div class="col-sm-12">
                    <div class="small-box bg-teal">
                        <div class="inner">
                            <h3>{{ formatKpiInteger(heroValue) }}</h3>
                            <p>{{ heroLabel || '\u00a0' }}</p>
                        </div>
                        <div class="icon">
                            <i class="fa fa-sitemap" />
                        </div>
                    </div>
                </div>
            </div>

            <h4 class="orders-by-portal-viewer__section-title">{{ _t('Revenue by portal') }}</h4>
            <div class="table-responsive orders-by-portal-viewer__table">
                <table class="table table-striped table-condensed">
                    <thead>
                        <tr>
                            <th v-for="column in columns" :key="column">
                                {{ columnLabel(column) }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="portalRows.length === 0">
                            <td :colspan="columns.length" class="text-muted">
                                {{ kpiPayload ? _t('No portal data for this period.') : '\u00a0' }}
                            </td>
                        </tr>
                        <tr v-for="row in portalRows" :key="row.portal">
                            <td v-for="column in columns" :key="column">
                                {{ formatCell(column, row) }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div v-if="chartable" class="box box-primary dashboard-chart-panel">
                <div class="box-header with-border">
                    <h3 class="box-title">{{ _t('Created orders by portal') }}</h3>
                </div>
                <div class="box-body">
                    <dashboard-apex-chart
                        v-if="kpiChart"
                        :chart="kpiChart"
                        :chart-type="chartType"
                    />
                    <div v-else class="dashboard-apex-chart" aria-hidden="true" />
                </div>
            </div>
            <div v-if="kpiLoading" class="kpi-stage__overlay">
                <div class="kpi-stage__overlay-inner">
                    <i class="fa fa-spinner fa-spin" aria-hidden="true" />
                    <span class="kpi-stage__overlay-label">{{ _t('Loading...') }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.orders-by-portal-viewer__section-title {
    margin: 0 0 10px;
    font-size: 14px;
    font-weight: 600;
    color: #555;
}
</style>
